import { createAsyncThunk } from '@reduxjs/toolkit';
import FileSaver from 'file-saver';
import api from 'src/app/api';
import { buildEncodedQueryString } from 'src/common';
import { DownloadTemplatePayload, ReportTemplate, SaveReportTemplateFile } from './types';

export enum TemplateReportsActionTypes {
  FetchReportTemplates = '@@client/templates/reports/FetchReportTemplates',
  SaveReportTemplateFile = '@@client/templates/reports/SaveReportTemplateFile',
  DownloadReportTemplateFile = '@@client/templates/reports/DownloadReportTemplateFile',
}

export enum TemplateReportsApiEndpoints {
  FetchReportTemplates = '/reporting/GetReportTemplateList',
  SaveReportTemplateFile = '/reporting/SaveReportTemplateFile',
  DownloadReportTemplateFile = '/reporting/DownloadReportTemplateById',
}

export const fetchReportTemplates = createAsyncThunk<ReportTemplate[]>(TemplateReportsActionTypes.FetchReportTemplates, async () => {
  const response = await api.get<ReportTemplate[]>(TemplateReportsApiEndpoints.FetchReportTemplates);
  return response.data;
});

export const downloadReportTemplateFile = createAsyncThunk(
  TemplateReportsActionTypes.DownloadReportTemplateFile,
  async (payload: DownloadTemplatePayload) => {
    const queryString = buildEncodedQueryString({
      id: payload.id,
      templateFormat: payload.templateFormat,
    });

    const response = await api.get<File>(`${TemplateReportsApiEndpoints.DownloadReportTemplateFile}${queryString}`, {
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    const name = payload.templateFormat === 'Excel' ? payload.excelTemplateFileName : payload.wordTemplateFileName;
    const fileName = contentDisposition ?? name;
    FileSaver.saveAs(new Blob([response.data]), fileName);
  }
);

export const saveReportTemplateFile = createAsyncThunk<ReportTemplate, SaveReportTemplateFile>(
  TemplateReportsActionTypes.SaveReportTemplateFile,
  async (payload: SaveReportTemplateFile, thunkApi) => {
    const form = new FormData();
    if (payload.file) {
      form.append('file', payload.file);
    }
    form.append('id', payload.id || '');
    form.append('name', payload.name);
    form.append('wordOrExcel', payload.templateFormat);
    return api
      .post<ReportTemplate>(TemplateReportsApiEndpoints.SaveReportTemplateFile, form)
      .then((response) => {
        return response.data;
      })
      .catch(() => {
        return thunkApi.rejectWithValue({
          message: 'Could not upload report template file at this time.',
          variant: 'error',
        });
      });
  }
);
